/**
 * Rentcast API Service
 * Fetches market rental data by zip code with localStorage caching
 * API Docs: https://developers.rentcast.io/reference/market-statistics
 */

import { trackApiCall, trackCacheHit } from '../utils/telemetry';

const API_BASE_URL = 'https://api.rentcast.io/v1';
const API_KEY = process.env.REACT_APP_RENTCAST_API_KEY;
const CACHE_KEY_PREFIX = 'rentcast_market_';
const CACHE_DURATION_DAYS = 7; // Cache data for 7 days

/**
 * Check if Rentcast API is configured
 */
export function isApiConfigured() {
  return API_KEY && API_KEY.length > 0 && API_KEY !== 'your_api_key_here';
}

/**
 * Check if we should use static data (for development/fallback)
 */
export function useStaticData() {
  return process.env.REACT_APP_USE_STATIC_DATA === 'true' || !isApiConfigured();
}

/**
 * Generate cache key for a specific query
 */
function getCacheKey(zipCode, bedrooms) {
  return `${CACHE_KEY_PREFIX}${zipCode}_${bedrooms}br`;
}

/**
 * Get cached data if still valid
 * @returns {Object|null} Cached data with timestamp or null if expired/missing
 */
export function getCachedData(zipCode, bedrooms) {
  try {
    const cacheKey = getCacheKey(zipCode, bedrooms);
    const cached = localStorage.getItem(cacheKey);

    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const cacheAge = Date.now() - timestamp;
    const maxAge = CACHE_DURATION_DAYS * 24 * 60 * 60 * 1000;

    if (cacheAge > maxAge) {
      // Cache expired
      localStorage.removeItem(cacheKey);
      return null;
    }

    return { data, timestamp, isFromCache: true };
  } catch (e) {
    console.warn('Cache read error:', e);
    return null;
  }
}

/**
 * Save data to cache
 */
function setCachedData(zipCode, bedrooms, data) {
  try {
    const cacheKey = getCacheKey(zipCode, bedrooms);
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  } catch (e) {
    console.warn('Cache write error:', e);
  }
}

/**
 * Clear cache for a specific query (for manual refresh)
 */
export function clearCache(zipCode, bedrooms) {
  try {
    const cacheKey = getCacheKey(zipCode, bedrooms);
    localStorage.removeItem(cacheKey);
  } catch (e) {
    console.warn('Cache clear error:', e);
  }
}

/**
 * Clear all Rentcast cache
 */
export function clearAllCache() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn('Cache clear all error:', e);
  }
}

/**
 * Format timestamp for display
 */
export function formatCacheTimestamp(timestamp) {
  if (!timestamp) return null;

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffDays === 0) {
    if (diffHours === 0) {
      return 'Just now';
    }
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
}

/**
 * Fetch market data from Rentcast API by zip code
 * @param {string} zipCode - 5-digit zip code
 * @param {number} bedrooms - Number of bedrooms (for cache key)
 * @param {boolean} forceRefresh - Skip cache and fetch fresh data
 * @returns {Promise<Object>} Market data with timestamp
 */
export async function fetchMarketData(zipCode, bedrooms, forceRefresh = false) {
  if (!isApiConfigured()) {
    throw new Error('Rentcast API key not configured');
  }

  // Check cache first (unless forcing refresh)
  if (!forceRefresh) {
    const cached = getCachedData(zipCode, bedrooms);
    if (cached) {
      console.log(`Using cached data for ${zipCode} ${bedrooms}BR (${formatCacheTimestamp(cached.timestamp)})`);
      trackCacheHit();
      window.gtag?.('event', 'api_cache_hit', {
        event_category: 'api',
        zip_code: zipCode,
        bedrooms,
      });
      return cached;
    }
  }

  console.log(`Fetching fresh data from Rentcast API for ${zipCode}...`);

  const params = new URLSearchParams({
    zipCode,
    dataType: 'Rental',
    historyRange: '6' // 6 months of history
  });

  const startTime = Date.now();
  let success = false;

  try {
    const response = await fetch(`${API_BASE_URL}/markets?${params}`, {
      method: 'GET',
      headers: {
        'X-Api-Key': API_KEY,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const rawData = await response.json();
    const marketData = transformMarketResponse(rawData, zipCode, bedrooms);
    success = true;
    const duration = Date.now() - startTime;
    trackApiCall('rentcast/markets', true, duration);
    window.gtag?.('event', 'api_call', {
      event_category: 'api',
      endpoint: 'rentcast/markets',
      success: true,
      duration_ms: duration,
      zip_code: zipCode,
      bedrooms,
    });

    // Cache the result
    setCachedData(zipCode, bedrooms, marketData);

    return {
      data: marketData,
      timestamp: Date.now(),
      isFromCache: false
    };
  } catch (error) {
    const duration = Date.now() - startTime;
    trackApiCall('rentcast/markets', false, duration);
    window.gtag?.('event', 'api_call', {
      event_category: 'api',
      endpoint: 'rentcast/markets',
      success: false,
      duration_ms: duration,
      zip_code: zipCode,
      bedrooms,
    });
    throw error;
  }
}

/**
 * Transform Rentcast market API response to match our app's data structure
 *
 * API Response structure:
 * {
 *   id: "19125",
 *   zipCode: "19125",
 *   rentalData: {
 *     medianRent: 1800,
 *     minRent: 675,
 *     maxRent: 9272,
 *     totalListings: 545,
 *     dataByBedrooms: [
 *       { bedrooms: 0, medianRent: 1295, minRent: 675, maxRent: 3250, totalListings: 59 },
 *       { bedrooms: 1, medianRent: 1535, ... },
 *       { bedrooms: 2, medianRent: 1900, ... },
 *       ...
 *     ]
 *   }
 * }
 */
function transformMarketResponse(rawData, zipCode, bedrooms) {
  // Get rental data section
  const rentalData = rawData.rentalData || rawData;

  // Find bedroom-specific data from dataByBedrooms array
  let bedroomData = null;
  if (rentalData.dataByBedrooms && Array.isArray(rentalData.dataByBedrooms)) {
    bedroomData = rentalData.dataByBedrooms.find(d => d.bedrooms === bedrooms);
  }

  // Use bedroom-specific data if available, otherwise fall back to aggregate
  const medianRent = bedroomData?.medianRent || rentalData.medianRent || null;
  const lowRange = bedroomData?.minRent || rentalData.minRent || (medianRent ? Math.round(medianRent * 0.85) : null);
  const highRange = bedroomData?.maxRent || rentalData.maxRent || (medianRent ? Math.round(medianRent * 1.15) : null);
  const sampleSize = bedroomData?.totalListings || rentalData.totalListings || 20;

  // Build our market data structure
  return {
    medianRent,
    lowRange,
    highRange,
    sampleSize,
    neighborhood: rawData.city || rawData.neighborhood || `Zip ${zipCode}`,
    zipCode,
    bedrooms,
    lastUpdated: rentalData.lastUpdatedDate
      ? new Date(rentalData.lastUpdatedDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    dataSource: 'Rentcast API (Live)',
    // Additional useful data
    averageRent: bedroomData?.averageRent || rentalData.averageRent,
    medianDaysOnMarket: bedroomData?.medianDaysOnMarket || rentalData.medianDaysOnMarket,
    newListings: bedroomData?.newListings || rentalData.newListings
  };
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(k => k.startsWith(CACHE_KEY_PREFIX));

    const stats = cacheKeys.map(key => {
      const cached = JSON.parse(localStorage.getItem(key));
      const keyParts = key.replace(CACHE_KEY_PREFIX, '').split('_');
      return {
        zipCode: keyParts[0],
        bedrooms: keyParts[1],
        timestamp: cached.timestamp,
        age: formatCacheTimestamp(cached.timestamp)
      };
    });

    return {
      count: stats.length,
      entries: stats
    };
  } catch (e) {
    return { count: 0, entries: [] };
  }
}

export default {
  isApiConfigured,
  useStaticData,
  fetchMarketData,
  getCachedData,
  clearCache,
  clearAllCache,
  formatCacheTimestamp,
  getCacheStats
};
